import { ReactNode, useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CodeInjection } from "@/components/layout/CodeInjection";
import { TrailingSlashRedirect } from "@/components/layout/TrailingSlashRedirect";
import { SiteShell } from "@/components/layout/SiteShell";
import { AuthProvider } from "@/hooks/useAuth";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";

import appCss from "../styles.css?url";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: () => ({
      meta: [
        { charSet: "UTF-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1.0" },
        { title: "Cedar Creek Construction | Roofing & Siding in Minnesota" },
        {
          name: "keywords",
          content:
            "roofing, siding, deck installation, gutters, roof replacement, roof repair, siding replacement, Minneapolis, Minnesota, Cedar Creek Construction",
        },
        { name: "author", content: "Cedar Creek Construction" },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Cedar Creek Construction" },
        {
          property: "og:title",
          content:
            "Cedar Creek Construction | Roofing & Siding in Minneapolis, MN",
        },
        {
          property: "og:description",
          content:
            "Expert roofing, siding, deck & gutter services in Minneapolis, MN. Licensed contractor #BC645307. Call (612) 298-9865 for a free estimate.",
        },
        {
          property: "og:image",
          content: "https://cedarcreekconstruct.com/og-image.png",
        },
        { name: "twitter:card", content: "summary_large_image" },
        {
          name: "twitter:title",
          content:
            "Cedar Creek Construction | Roofing & Siding in Minneapolis, MN",
        },
        {
          name: "twitter:description",
          content:
            "Expert roofing, siding, deck & gutter services in Minneapolis, MN. Licensed contractor #BC645307. Call (612) 298-9865 for a free estimate.",
        },
        {
          name: "twitter:image",
          content: "https://cedarcreekconstruct.com/og-image.png",
        },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        // Google Fonts
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "preload",
          href: "https://fonts.googleapis.com/css2?family=Sora:wght@700&display=swap",
          as: "style",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap",
        },
        // Third-party origins
        {
          rel: "preconnect",
          href: "https://elfsightcdn.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "preconnect",
          href: "https://universe-static.elfsightcdn.com",
          crossOrigin: "anonymous",
        },
        { rel: "dns-prefetch", href: "https://elfsightcdn.com" },
        {
          rel: "dns-prefetch",
          href: "https://universe-static.elfsightcdn.com",
        },
        // Favicons - Cedar Creek Construction logo
        {
          rel: "icon",
          type: "image/png",
          sizes: "48x48",
          href: "/favicon.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "192x192",
          href: "/favicon.png",
        },
        { rel: "shortcut icon", type: "image/x-icon", href: "/favicon.ico" },
        {
          rel: "apple-touch-icon",
          sizes: "180x180",
          href: "/apple-touch-icon.png",
        },
      ],
      scripts: [
        // Skill Mammoth portal analytics (Website Traffic panel), cookie-free
        { src: "https://newportal.skillmammoth.com/sm.js", "data-site": "f603149bfd52427f", defer: true },
        // Google tag (gtag.js) — GA4
        {
          src: "https://www.googletagmanager.com/gtag/js?id=G-V39E1XV19G",
          async: true,
        },
        {
          children:
            "window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-V39E1XV19G');",
        },
        // Google Ads tag (gtag.js)
        {
          src: "https://www.googletagmanager.com/gtag/js?id=AW-11151203158",
          async: true,
        },
        {
          children:
            "window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'AW-11151203158');",
        },
      ],
    }),
    shellComponent: RootShell,
    component: RootComponent,
    notFoundComponent: () => (
      <SiteShell>
        <NotFound />
      </SiteShell>
    ),
    errorComponent: RootErrorComponent,
  },
);

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <CodeInjection />
          <TrailingSlashRedirect />
          <Outlet />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const router = useRouter();

  console.error(error);

  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-xl font-bold">This page didn't load</h1>
        <p className="text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back
          home.
        </p>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            className="px-4 py-2 rounded-md border border-border bg-background text-foreground"
            href="/"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
